import React, { useState } from 'react';
import { Box, Card, TextField, Button, Typography, Alert, InputAdornment, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { VisibilityRounded as Visibility, VisibilityOffRounded as VisibilityOff } from '@mui/icons-material';
import { getApiUrl } from '../config';
import { tokens } from '../theme';
import { Logo } from './AppShell';

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${getApiUrl()}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();

      if (data.success) {
        localStorage.setItem('auth_token', data.token);
        onLogin(data.token);
      } else {
        setError(data.message || 'Wrong username or password');
      }
    } catch (err) {
      setError('Can\'t reach the server. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'grid', placeItems: 'center', px: 2, py: 6,
      background: `radial-gradient(900px 500px at 15% -10%, ${alpha(tokens.accent, 0.22)}, transparent 60%),
                   radial-gradient(700px 500px at 110% 110%, ${alpha(tokens.outcome.away, 0.16)}, transparent 60%), ${tokens.bg}`,
    }}>
      <Box width="100%" maxWidth={400}>
        <Box display="flex" justifyContent="center" mb={4}><Logo size={36} /></Box>
        <Card sx={{ p: { xs: 3, sm: 4 }, bgcolor: alpha(tokens.surface, 0.85), backdropFilter: 'blur(12px)' }}>
          <Typography variant="h5" component="h1" gutterBottom>Welcome back</Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Sign in to see today's predictions and arbitrage opportunities.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              required
              fullWidth
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button type="submit" variant="contained" size="large" disabled={loading || !username || !password} sx={{ mt: 1, py: 1.25 }}>
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default Login;
